import React, { Component } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dataUtils from '../../../util/dataUtils';
import { isWhite } from '../../../util/colors';
import { escapeHTML } from '../../../util/text';

import highchartRegression from '../../../util/highcharts-regression';
import highchartFactory from '../../../util/highcharts';
import { formatWorkDuration } from '../../../util/dates';
const highchartUtils = highchartFactory(Highcharts);

let chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'scatter',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  time: {
    useUTC: false
  },
  xAxis: {
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    tickmarkPlacement: 'on',
    title: {
      text: 'Cycle time',
      style: {
        fontSize: '13px'
      }
    }
  },
  yAxis: {
    min: 0,
    opposite: false,
    allowDecimals: false,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle time',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    plotLines: []
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      cursor: 'pointer',
      point: {
        events: {}
      }
    },
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 7,
        states: {
          hover: {
            lineColor: 'rgb(100,100,100)'
          }
        }
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  series: []
};

export class DDPChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: {}
    };
  }

  static defaultProps = {
    onPointClick: function() {}
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartRegression(Highcharts);

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    chartConfig.exporting = this.props.chartExporting;

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    if (this.props.data.series) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart(prevProps.data);
    }

    if (prevProps.timeUnit != this.props.timeUnit) {
      let obj = {
        xAxis: {
          title: {
            text: `Cycle time (${this.props.timeUnit})`
          }
        },
        yAxis: {
          title: {
            text: `Predicted time (${this.props.timeUnit})`
          }
        }
      };
      this.chart.update(obj);
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Due Date Performance Chart',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart(oldData = {}) {
    this.chart.xAxis[0].setExtremes(null, null);

    const diff = dataUtils.getDiff(oldData.series, this.props.data.series, 'data');
    highchartUtils.updateSeries(diff, this.chart);

    this.chart.update(
      {
        xAxis: {
          title: {
            text: `Cycle time (${this.props.timeUnit}s)`
          }
        },
        yAxis: {
          title: {
            text: `Predicted time (${this.props.timeUnit}s)`
          }
        }
      },
      false
    );

    this.chart.redraw();
    this.updateRegression();
  }

  updateRegression() {
    const trend = _.find(this.chart.series, { name: 'trend' });
    if (trend) {
      this.chart.series[trend.index].remove(true);
    }

    const data = _([...this.props.data.series])
      .flatMap('data')
      .sortBy('x')
      .value();

    this.chart.addSeries(
      {
        data,
        visible: false,
        regression: true,
        regressionSettings: {
          name: 'trend',
          index: 0,
          useAllSeries: true,
          type: 'linear',
          color: 'rgb(87, 181, 70)',
          lineWidth: 3,
          dashStyle: 'LongDashDot'
        }
      },
      true
    );
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('ddp', this.props.wallmode);
    this.chart.update(newConfig.chart);
  }

  tooltipFormatter(comp, ev) {
    if (ev.series.name === 'trend') {
      return '<div></div>';
    }

    const ct = formatWorkDuration(
      ev.point.cycleTimeInMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );
    const estimated = formatWorkDuration(
      ev.point.estimatedMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );
    let tooltip = `<p class="tooltip-label-size" style="margin: 4px 8px 0;">Cycle time: ${ct}</p> <p class="tooltip-label-size" style="margin: 8px 8px 0;">Predicted time: ${estimated}</p>`;

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        const point = comp.chart.series[s].data[i];
        if (point.card && point.x === ev.x && point.y === ev.y) {
          const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
          tooltip += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
        }
      }
    }

    tooltip += `<p class="tooltip-info-txt">Click on the dot to see the details</p>`;

    return tooltip;
  }

  onPointClick(comp, ev) {
    const points = [];
    const cycleTime = formatWorkDuration(
      ev.cycleTimeInMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );
    const estimated = formatWorkDuration(
      ev.estimatedMinutes,
      comp.props.excludeWeekendOpts,
      comp.props.timePrecision,
      comp.props.useWorkingHoursInCycleTimeDays
    );

    for (let s = 0; s < comp.chart.series.length; s++) {
      for (let i = 0; i < comp.chart.series[s].data.length; i++) {
        const point = comp.chart.series[s].data[i];
        if (point.card && point.x === ev.x && point.y === ev.y) {
          points.push({
            card: point.card,
            details: point.details,
            color: point.color,
            date: point.date,
            estimatedMinutes: point.estimatedMinutes
          });
        }
      }
    }

    comp.props.onPointClick({
      estimated,
      cycleTime,
      points
    });
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div
          id={`${this.props.id}-details`}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    timePrecision: state.filters.timePrecision,
    timeUnit: state.filters.ddp.timeUnit,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps)(DDPChart));
